import {
  EOrganization,
  ERef,
  ERequestTypes,
  ESnapshotExists,
  EStripeBankAccount,
  EStripePaymentMethodCard,
  EStripePaymentMethodUSBankAccount,
  EUser
} from 'lib/types';
import api from 'api';

export type UserPaymentMethods = {
  bankAccounts: EStripeBankAccount[];
  bankAccountsPaymentMethods: EStripePaymentMethodUSBankAccount[];
  cards: EStripePaymentMethodCard[];
};

export type CustomerBankAccounts = Omit<UserPaymentMethods, 'cards'>;

export const getInvoicePaymentMethods = async (
  invoiceId: string
): Promise<UserPaymentMethods> => {
  const req: ERequestTypes['payments/list-invoice-payment-methods'] = {
    invoiceId
  };

  const { error, response: res } = await api.safePost(
    'payments/list-invoice-payment-methods',
    req
  );

  if (error || !res) {
    throw new Error(error || 'Failed to get invoice payment methods');
  }

  return {
    cards: res.cards,
    bankAccounts: res.bank_accounts_sources,
    bankAccountsPaymentMethods: res.bank_accounts_payment_methods
  };
};

/**
 * Helper to get the bank accounts a user can access via their own Stripe customer
 * or their organization's.
 *
 * @param user the active user.
 * @param organization if specified this overrides the user's active organization
 * as the organization to use for locating bank accounts.
 */
export const getCustomerBankAccounts = async (
  user: ESnapshotExists<EUser>,
  organization?: ERef<EOrganization>
): Promise<CustomerBankAccounts> => {
  const org = organization ?? user.data().activeOrganization;
  const req: ERequestTypes['payments/list-customer-bank-accounts'] = org
    ? { type: 'organization', organizationId: org.id }
    : { type: 'individual' };

  const { error, response: res } = await api.safePost(
    'payments/list-customer-bank-accounts',
    req
  );

  if (error || !res) {
    throw new Error(error || 'Failed to get customer bank accounts');
  }

  return {
    bankAccounts: res.bank_accounts_sources,
    bankAccountsPaymentMethods: res.bank_accounts_payment_methods
  };
};
